import React from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { Skeleton } from '@/design-system'
import { GallerySummary } from '../../types'
import { GalleryCard } from '../GalleryCard'
import { GalleryListRow } from '../GalleryListRow'

export type GalleryViewMode = 'grid' | 'list'

export interface GalleryGridProps {
  galleries: GallerySummary[]
  loading?: boolean
  view: GalleryViewMode
  onSelect?: (gallery: GallerySummary) => void
  /** albumId → title, resolved from the shell's already-loaded albums (no extra request). */
  sourceAlbumTitles?: Record<string, string>
  /** albumId → coverUrl, same source — stand-in art until a gallery's own scene note loads. */
  sourceAlbumCovers?: Record<string, string>
  /** Shows the dashed "New gallery" tile as the trailing grid cell (grid view only). Defaults to true. */
  showNewTile?: boolean
  /** Fires an instant stub-create + redirect into Studio (Option D) — falls back to /albums if omitted. */
  onCreateNew?: () => void
}

const Grid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(258px, 1fr))',
  gap: 20,
  width: '100%',
})

const List = styled(Box)({
  background: 'var(--ds-canvas)',
  borderRadius: 'var(--ds-radius-lg)',
  boxShadow: 'var(--ds-shadow-soft)',
  overflow: 'hidden',
})

const NewTile = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  border: '1.5px dashed var(--ds-border-strong, var(--ds-border))',
  borderRadius: 'var(--ds-radius-lg)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  minHeight: 246,
  cursor: 'pointer',
  transition: `border-color var(--ds-duration-normal) var(--ds-ease), background var(--ds-duration-normal) var(--ds-ease)`,
  '&:hover': {
    borderColor: 'var(--ds-ink)',
    background: 'var(--ds-canvas)',
  },
  '&:hover .new-tile-plus': { transform: 'rotate(90deg)' },
})

const Plus = styled(Box)({
  width: 40,
  height: 40,
  borderRadius: '50%',
  background: 'var(--ds-inverse)',
  color: 'var(--ds-inverse-ink)',
  fontSize: 18,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: `transform var(--ds-duration-normal) var(--ds-ease)`,
})

const NewTitle = styled(Box)({ fontSize: 13.5, fontWeight: 650, color: 'var(--ds-ink)' })
const NewSubtitle = styled(Box)({ fontSize: 11.5, color: 'var(--ds-ink-subtle)', textAlign: 'center', padding: '0 20px' })

export function GalleryGrid({
  galleries,
  loading,
  view,
  onSelect,
  sourceAlbumTitles,
  sourceAlbumCovers,
  showNewTile = true,
  onCreateNew,
}: GalleryGridProps) {
  const navigate = useNavigate()

  if (loading) {
    return (
      <Grid>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} height='auto' sx={{ aspectRatio: '4 / 3.05', width: '100%' }} rounded='lg' />
        ))}
      </Grid>
    )
  }

  if (view === 'list') {
    return (
      <List>
        {galleries.map((g) => (
          <GalleryListRow
            key={g.id}
            gallery={g}
            onClick={() => onSelect?.(g)}
            sourceAlbumTitle={g.sourceAlbumId ? sourceAlbumTitles?.[g.sourceAlbumId] : undefined}
            sourceAlbumCoverUrl={g.sourceAlbumId ? sourceAlbumCovers?.[g.sourceAlbumId] : undefined}
          />
        ))}
      </List>
    )
  }

  return (
    <Grid>
      {galleries.map((g, i) => (
        <GalleryCard
          key={g.id}
          gallery={g}
          index={i}
          onClick={() => onSelect?.(g)}
          sourceAlbumTitle={g.sourceAlbumId ? sourceAlbumTitles?.[g.sourceAlbumId] : undefined}
          sourceAlbumCoverUrl={g.sourceAlbumId ? sourceAlbumCovers?.[g.sourceAlbumId] : undefined}
        />
      ))}
      {showNewTile && (
        <NewTile type='button' onClick={() => (onCreateNew ? onCreateNew() : navigate('/albums'))}>
          <Plus className='new-tile-plus'>+</Plus>
          <NewTitle>New gallery</NewTitle>
          <NewSubtitle>Lands you in the room instantly — pick photos once you're in</NewSubtitle>
        </NewTile>
      )}
    </Grid>
  )
}
